<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const { t } = useI18n();
const localePath = useLocalePath();

const route = useRoute();

const buildingQuery = gql`
  query getBuildings($slug: String!) {
    building(slug: $slug) {
      slug: seo_url_slug {
        cs
        en
      }
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      id
      name
      image_detail
      content
      perex
      address
      transport
      alert_text
      google_maps_url
      openingHours {
        from
        to
        day_in_week
      }
      features {
        type
        title
      }
      warnings {
        local
        local_color
        local_text_color
      }
    }
  }
`;

const { data } = await useAsyncQuery(buildingQuery, { slug: route.params.slug });
const building = ref(data.value?.building);
const content = ref(JSON.parse(data.value?.building.content));

const setI18nParams = useSetI18nParams();

setI18nParams({
  en: {
    slug: building?.value?.slug?.en,
  },
  cs: {
    slug: building?.value?.slug?.cs,
  },
});

const breadcrumbs = ref([
  {
    title: t('pages.about.index.breadcrumbs-title'),
    route: 'about',
  },
  {
    title: t('pages.buildings.index.breadcrumbs-title'),
    route: 'about-buildings',
  },
  { title: t('pages.buildings.detail.breadcrumbs-title') },
]);

useHeadData({
  title: building.value?.seo_meta_title || building.value?.name || null,
  description: building.value?.seo_meta_description || null,
  keywords: building.value?.seo_meta_keywords || null,
  image: building.value?.image_detail || building.value?.cropped_detail_image || null,
  url: route.fullPath,
});
</script>

<template>
  <main class="main">
    <LocalWarning
      v-if="building && building.warnings.local"
      :text="useBlocks(building?.warnings?.local)[0]?.data?.text"
      :color="building.warnings?.local_color"
      :textcolor="building.warnings?.local_text_color"
    />
    <Breadcrumbs :items="breadcrumbs" />
    <div v-if="building">
      <span class="text text--2 fwm upper">{{ t('pages.buildings.detail.building') }}</span>
      <h1 class="heading mt-1">{{ building.name }}</h1>
      <p class="text text--2 mt-2">
        {{ building.address }}
        <a v-if="building.google_maps_url" :href="building.google_maps_url" target="_blank" class="text--3 ml-2 link">{{ t('pages.buildings.detail.map') }}</a>
      </p>
      <div class="container-max-content">
        <div class="mt-4 mt-md-5 b-flex">
          <div md="6" class="b-flex-child">
            <RatioImage :src="useGetImageUrl(building.image_detail, 1000, 600)" :alt="building.name" />
          </div>
          <div class="mt-4 mt-md-0 b-flex-child">
            <p class="text text--2" v-html="useOpeningHours(building.openingHours)" />
            <p class="text text--2 mt-3 pre-line">{{ building.transport }}</p>
            <p class="text text--2 mt-3">
              <nuxt-link :to="localePath('about-accessibility')" class="link">{{ t('pages.buildings.detail.accessibility-info') }}</nuxt-link>
            </p>
            <div class="mt-3">
              <span v-for="(item, key) in building.features" :key="key">
                <FeatureIcon v-if="item" :data="item" />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="container-content">
        <h2 class="heading heading--2 heading--thin mt-5">{{ t('pages.buildings.detail.about') }}</h2>
        <DynamicPageContent :content="content.blocks" class="mt-3" />
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.pre-line {
  white-space: pre-line;
}

.b-flex {
  align-items: center;
}
</style>
